import type { InventoryListByIdAPI } from '@openx/types/inventoryList';
import type { AppInventoryVersionType } from '@openx/types/targeting/appInventory';
import {
  type InventoryAndContentState,
  InventoryContentOption,
  type ParentAccountFeatures,
} from '@openx/types/targeting/inventoryAndContent';
import type { TargetingFieldsValues, TargetingReadOnlyProps } from '@openx/types/targeting/targeting';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useTargetingContext } from '../../utils/context/TargetingContext';
import { useTargetingDisabledState } from '../../utils/hooks/useTargetingDisabledState';
import { getEmptyInventoryState } from '../shared/InventoryAndContent/inventoryHelpers';
import { useDropdownOpen } from '../shared/ReadOnlyTargetingDropdown/hooks/useDropdownOpen';
import { StyledReadOnlyTargetingDropdown } from '../shared/ReadOnlyTargetingDropdown/ReadOnlyTargetingDropdown';
import { TargetingReadOnly } from '../shared/TargetingReadOnly/TargetingReadOnly';
import { InventoryAndContentFormPreviewItems } from './InventoryAndContentItems/InventoryAndContentFormPreviewItems';
import { InventoryAndContentItems } from './InventoryAndContentItems/InventoryAndContentItems';
import type { SetInventoryListItemsDispatch } from './InventoryAndContentTopBox/constants';
import { getSelectedOptionsCount } from './utils/getSelectedOptionsCount';

type InventoryAndContentReadOnlyProps = TargetingReadOnlyProps<InventoryAndContentState> & {
  setInventoryListItems?: SetInventoryListItemsDispatch;
  inventoryListItems?: InventoryListByIdAPI['values'];
  parentAccountFeatures: ParentAccountFeatures;
  onRemoveParameter?: (option: InventoryContentOption) => void;
  field?: TargetingFieldsValues;
  userChangedInitialValue?: boolean;
  inventoryVersion?: AppInventoryVersionType;
};

export const InventoryAndContentReadOnly = ({
  name,
  field,
  userChangedInitialValue,
  onOpenDrawerClick,
  onRemoveClick,
  targetingParams,
  readonly,
  setInventoryListItems,
  inventoryListItems,
  parentAccountFeatures,
  onRemoveParameter,
  inventoryVersion,
}: InventoryAndContentReadOnlyProps) => {
  const { isDisabledOrHasError, disabledReasonMessage } = useTargetingDisabledState(field);

  const { useCurrentInstanceState, isOa } = useTargetingContext();
  const { t } = useTranslation();

  const isEmpty = useMemo(() => {
    // check if targetingParams contains any elements
    return (
      !targetingParams ||
      !(
        Object.values(targetingParams).some(t => {
          return (t.val && Object.keys(t.val).length) || (isOa && t?.inventorylist_id);
        }) ||
        targetingParams[InventoryContentOption.INVENTORY].excludes.length ||
        targetingParams[InventoryContentOption.INVENTORY].includes.length ||
        targetingParams[InventoryContentOption.PUBLISHER_INVENTORY_RULES].length
      )
    );
  }, [targetingParams, isOa]);

  const selectedOptionsCount = useMemo(() => getSelectedOptionsCount(targetingParams), [targetingParams]);

  const { isBefInstance } = useCurrentInstanceState();

  const hasValue = selectedOptionsCount > 0;
  const { isOpen, toggleOpen } = useDropdownOpen(hasValue);

  if (!readonly)
    return (
      <TargetingReadOnly
        name={name}
        field={field}
        userChangedInitialValue={userChangedInitialValue}
        isEmpty={isEmpty}
        onOpenDrawerClick={onOpenDrawerClick}
        onRemove={onRemoveClick}
        readonly={readonly}
        isDisabled={isDisabledOrHasError}
        description={t(
          'Target specific Inventory attributes that define where and how ads will appear within the publisher ecosystem.'
        )}
        optionsCount={selectedOptionsCount}
        isOpen={isOpen}
        toggleOpen={toggleOpen}
        disabledReasonMessage={disabledReasonMessage}
      >
        {isOpen && (
          <StyledReadOnlyTargetingDropdown>
            <InventoryAndContentFormPreviewItems
              targetingParams={targetingParams ?? getEmptyInventoryState()}
              isBefInstance={isBefInstance}
              appBundleIdDuplicateErrors={null}
              setInventoryListItems={setInventoryListItems}
              inventoryListItems={inventoryListItems}
              onOpenDrawerClick={onOpenDrawerClick}
              onRemove={onRemoveClick}
              onRemoveParameter={onRemoveParameter}
              readonly={true}
              field={field}
              parentAccountFeatures={parentAccountFeatures}
              inventoryVersion={inventoryVersion}
              optionsCount={selectedOptionsCount}
            />
          </StyledReadOnlyTargetingDropdown>
        )}
      </TargetingReadOnly>
    );

  return (
    <TargetingReadOnly
      name={name}
      field={field}
      userChangedInitialValue={userChangedInitialValue}
      isEmpty={isEmpty}
      onOpenDrawerClick={onOpenDrawerClick}
      onRemove={onRemoveClick}
      readonly={readonly}
      isDisabled={isDisabledOrHasError}
      description={t(
        'Target specific Inventory attributes that define where and how ads will appear within the publisher ecosystem.'
      )}
      disabledReasonMessage={disabledReasonMessage}
    >
      {!isEmpty && targetingParams && (
        <InventoryAndContentItems
          targetingParams={targetingParams}
          isBefInstance={isBefInstance}
          appBundleIdDuplicateErrors={null}
          setInventoryListItems={setInventoryListItems}
          inventoryListItems={inventoryListItems}
          inventoryVersion={inventoryVersion}
          readonly={true}
          field={field}
        />
      )}
    </TargetingReadOnly>
  );
};
